import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  HelpCircle,
  Receipt,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAddTaxPayment,
  useTaxEvent,
  useUpdateTaxEvent,
} from "@/lib/api/taxes.queries";
import type { TaxDueDateSource, TaxEventStatus } from "@/lib/api/taxes";
import { useActiveCompany } from "@/lib/company-context";
import { fmtDate } from "@/components/inventory/format";
import { TaxDeclarationCard } from "@/components/taxes/TaxDeclarationCard";
import { TAX_STATUS_LABEL, TaxEventBadges } from "@/components/taxes/TaxEventBadges";

export const Route = createFileRoute("/companies/$companyId/impuestos/$eventId")({
  component: TaxEventPage,
});

const DUE_DATE_SOURCE_LABEL: Record<TaxDueDateSource, string> = {
  official_calendar: "calendario oficial DIAN",
  estimated: "fecha estimada",
  accountant_override: "fecha del contador",
  manual: "fin del periodo",
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function TaxEventPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TaxEventContent />
      </AppShell>
    </RequireAuth>
  );
}

function TaxEventContent() {
  const fmtCop = useMoneyFormatter();
  const { companyId, eventId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const eventQuery = useTaxEvent(companyId, eventId);
  const updateEvent = useUpdateTaxEvent(companyId, eventId);
  const addPayment = useAddTaxPayment(companyId, eventId);
  const event = eventQuery.data;

  const [status, setStatus] = useState<TaxEventStatus>("projected");
  const [dueDate, setDueDate] = useState("");
  const [declaredAmount, setDeclaredAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso);
  const [paymentReference, setPaymentReference] = useState("");

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!event) return;
    setStatus(event.status);
    setDueDate(event.dueDate);
    setDeclaredAmount(
      event.declaredAmount === null ? "" : String(event.declaredAmount),
    );
    setNotes(event.notes ?? "");
    setPaymentAmount(String(Math.max(0, event.payableAmount - event.paidAmount)));
  }, [event]);

  function saveOverride(e: React.FormEvent) {
    e.preventDefault();
    updateEvent.mutate(
      {
        // Send the status and the due date only when they changed: saving
        // other fields must not rewrite them.
        status: status !== event?.status ? status : undefined,
        dueDate: dueDate !== event?.dueDate ? dueDate : undefined,
        declaredAmount: declaredAmount === "" ? null : Number(declaredAmount),
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => toast.success("Evento actualizado"),
        onError: (err) =>
          toast.error("No pudimos actualizar el evento", {
            description: err.message,
          }),
      },
    );
  }

  function savePayment(e: React.FormEvent) {
    e.preventDefault();
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Ingresa un valor de pago valido");
      return;
    }
    addPayment.mutate(
      {
        amount,
        paidAt: paymentDate,
        reference: paymentReference.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("Pago registrado");
          setPaymentReference("");
        },
        onError: (err) =>
          toast.error("No pudimos registrar el pago", {
            description: err.message,
          }),
      },
    );
  }

  if (eventQuery.isPending) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  if (eventQuery.isError || !event) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <ErrorState
          title="No pudimos cargar el evento"
          description={eventQuery.error?.message ?? ""}
          onRetry={() => void eventQuery.refetch()}
          retryLabel="Reintentar"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/impuestos"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver a impuestos
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Receipt className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              {event.displayName}
            </h1>
            <TaxEventBadges event={event} />
          </div>
          <p className="text-sm text-muted-foreground">
            Periodo {event.period.label} · vence {fmtDate(event.dueDate)}
            {` (${DUE_DATE_SOURCE_LABEL[event.dueDateSource]})`}
            {event.formCode ? ` · formulario ${event.formCode}` : ""}
          </p>
        </div>
      </header>

      <section className="grid gap-3 md:grid-cols-4">
        <Metric title="Proyectado" value={fmtCop(event.projectedAmount)} />
        <Metric title="Declarado" value={fmtCop(event.declaredAmount)} />
        <Metric title="Pagado" value={fmtCop(event.paidAmount)} />
        <Metric title="Saldo" value={fmtCop(Math.max(0, event.payableAmount - event.paidAmount))} />
      </section>

      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <main className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">De donde sale el valor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {event.projection.explanation}
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fuente</TableHead>
                    <TableHead>Detalle</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {event.projection.sourceBreakdown.map((source) => (
                    <TableRow key={source.key}>
                      <TableCell className="font-medium">{source.label}</TableCell>
                      <TableCell className="text-muted-foreground">{source.detail}</TableCell>
                      <TableCell className="text-right">{fmtCop(source.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HelpCircle className="h-4 w-4 text-primary" />
                Ayuda para entender y pagar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <h2 className="text-sm font-semibold">Que es</h2>
                <p className="mt-1 text-sm text-muted-foreground">{event.help.summary}</p>
              </div>
              <div>
                <h2 className="text-sm font-semibold">Por que aplica</h2>
                <p className="mt-1 text-sm text-muted-foreground">{event.help.whyApplies}</p>
              </div>
              <HelpList title="De donde viene" items={event.help.whereComesFrom} />
              <HelpList title="Como se paga" items={event.help.howToPay} />
              {event.help.forms.length ? (
                <div>
                  <h2 className="text-sm font-semibold">Formularios</h2>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {event.help.forms.map((form) => (
                      <Badge key={form} variant="outline">{form}</Badge>
                    ))}
                  </div>
                </div>
              ) : null}
              {event.help.officialLinks.length ? (
                <div>
                  <h2 className="text-sm font-semibold">Fuentes oficiales</h2>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {event.help.officialLinks.map((link) => (
                      <Button key={link.url} asChild variant="outline" size="sm">
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.label}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
              <Alert variant="warning">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Confirmacion contable</AlertTitle>
                <AlertDescription>{event.help.caution}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pagos registrados</CardTitle>
            </CardHeader>
            <CardContent>
              {event.payments.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {event.payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{fmtDate(payment.paidAt)}</TableCell>
                        <TableCell>{payment.reference || payment.method || "Sin referencia"}</TableCell>
                        <TableCell className="text-right">{fmtCop(payment.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Aun no hay pagos registrados para este evento.
                </p>
              )}
            </CardContent>
          </Card>
        </main>

        <aside className="space-y-6">
          {event.authority === "dian" ? (
            <TaxDeclarationCard companyId={companyId} eventId={event.id} />
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Actualizar evento</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="grid gap-4" onSubmit={saveOverride}>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-status">Estado</Label>
                  <Select value={status} onValueChange={(value) => setStatus(value as TaxEventStatus)}>
                    <SelectTrigger id="tax-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TAX_STATUS_LABEL).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-due">Fecha de vencimiento</Label>
                  <Input id="tax-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-declared">Valor declarado</Label>
                  <Input
                    id="tax-declared"
                    inputMode="decimal"
                    value={declaredAmount}
                    onChange={(e) => setDeclaredAmount(e.target.value)}
                    placeholder={String(event.projectedAmount)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-notes">Notas</Label>
                  <textarea
                    id="tax-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-24 rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <Button type="submit" disabled={updateEvent.isPending}>
                  <Save className="h-4 w-4" />
                  Guardar
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Registrar pago</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="grid gap-4" onSubmit={savePayment}>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-amount">Valor pagado</Label>
                  <Input
                    id="tax-payment-amount"
                    inputMode="decimal"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-date">Fecha</Label>
                  <Input
                    id="tax-payment-date"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-reference">Referencia</Label>
                  <Input
                    id="tax-payment-reference"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="Recibo, PSE o banco"
                  />
                </div>
                <Button type="submit" disabled={addPayment.isPending}>
                  <CheckCircle2 className="h-4 w-4" />
                  Registrar pago
                </Button>
              </form>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Metric(props: { title: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{props.title}</p>
      <p className="mt-2 text-xl font-semibold">{props.value}</p>
    </div>
  );
}

function HelpList(props: { title: string; items: string[] }) {
  return (
    <div>
      <h2 className="text-sm font-semibold">{props.title}</h2>
      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
        {props.items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
